"use client";

import React from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import { siteConfig } from "@/config/site.config";

const ConstellationCanvas = dynamic(
  () => import("./ConstellationCanvas").then((mod) => mod.ConstellationCanvas),
  { ssr: false }
);

export const HeroSection: React.FC = () => {
  const headline = siteConfig.hero.variants[siteConfig.hero.variant];

  return (
    <section className="relative min-h-screen flex items-center justify-between px-6 md:px-12 lg:px-20 pt-28 pb-16 overflow-hidden bg-[#030508]">
      <ConstellationCanvas />

      <div className="absolute inset-0 bg-[radial-gradient(rgba(197,160,89,0.06)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none [mask-image:radial-gradient(ellipse_at_center,transparent_10%,black_80%)] z-10" />

      <div className="absolute top-[30%] left-[20%] w-[350px] h-[350px] bg-radial from-indigo-500/5 to-transparent blur-3xl pointer-events-none rounded-full z-10" />
      <div className="absolute top-[20%] right-[15%] w-[450px] h-[450px] bg-radial from-gold/5 to-transparent blur-3xl pointer-events-none rounded-full z-10" />

      <div className="relative z-20 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center max-w-7xl mx-auto">
        <div className="lg:col-span-8 flex flex-col items-start text-left">
          
          <div className="flex items-center gap-4 mb-6">
            <span className="block w-12 h-[1px] bg-gold/50" />
            <p className="text-[10px] md:text-xs font-semibold tracking-[0.5em] text-gold uppercase">
              {siteConfig.hero.smallHeader}
            </p>
          </div>

          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-text-pure tracking-wide leading-tight mb-6 font-light">
            Sapere{" "}
            <span className="font-playfair italic font-normal text-gold pl-2">
              AUDE
            </span>
          </h1>

          <h2 className="font-serif text-xl sm:text-2xl md:text-3xl text-gold/90 font-light mb-6 tracking-wide">
            {headline}
          </h2>

          <p className="border-l border-gold/45 pl-6 text-sm md:text-base text-text-pure/75 leading-relaxed font-light tracking-wide max-w-2xl mb-10">
            {siteConfig.hero.subtitle}
          </p>

          <div className="flex flex-row flex-wrap gap-4 items-center">
            <Link
              href={siteConfig.hero.primaryButtonLink}
              className="inline-flex items-center justify-center gap-2 bg-gold text-bg-main px-8 py-3.5 font-sans font-semibold text-xs uppercase tracking-widest shadow-lg shadow-gold/20 hover:bg-gold-light hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 rounded"
            >
              {siteConfig.hero.primaryButtonText}
            </Link>

            <a
              href={siteConfig.hero.secondaryButtonLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 border border-gold/50 bg-gold/5 px-8 py-3.5 font-sans font-light text-xs text-gold uppercase tracking-[0.2em] hover:bg-gold/15 hover:border-gold active:scale-[0.98] transition-all duration-300 rounded"
            >
              <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              {siteConfig.hero.secondaryButtonText}
            </a>

            <Link
              href="/nosotros"
              className="font-sans font-light text-xs text-text-pure uppercase tracking-[0.25em] border-b border-text-pure/30 pb-1.5 hover:border-text-pure hover:text-gold transition-all duration-300"
            >
              Conoce el proyecto
            </Link>
          </div>
        </div>

        <div className="lg:col-span-4 flex flex-col items-center lg:items-end justify-end relative w-full h-[250px] lg:h-[450px] pointer-events-none">
          <div className="absolute bottom-4 lg:bottom-12 right-1/2 translate-x-1/2 lg:translate-x-0 lg:right-4 text-center lg:text-right flex flex-col gap-1 select-none">
            <p className="text-[8px] font-medium tracking-[0.4em] text-text-muted/50 uppercase">
              In Memoriam
            </p>
            <p className="text-xs font-serif font-light text-text-pure tracking-wider">
              {siteConfig.inMemoriam.nombre}
            </p>
            <div className="flex items-center justify-center lg:justify-end gap-3 mt-1 opacity-50">
              <p className="text-[10px] font-sans font-light text-text-pure/80 tracking-widest">
                {siteConfig.inMemoriam.anios}
              </p>
              <span className="block w-6 h-[1px] bg-gold" />
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-[#030508] to-transparent pointer-events-none z-20" />
    </section>
  );
};
